import { unref } from 'vue';
import { useStore } from './store';

const getInternalDownloadUrl = attachment => {
  return (
    attachment?.internal_download_url || attachment?.internalDownloadUrl || ''
  );
};

const getAttachmentId = attachment => attachment?.id ?? null;

const getAttachmentMessageId = attachment => {
  return attachment?.message_id ?? attachment?.messageId ?? null;
};

const getAttachmentDataUrl = attachment => {
  return attachment?.data_url || attachment?.dataUrl || '';
};

const matchesAttachment = (candidate, attachment) => {
  if (!candidate || !attachment) return false;

  const candidateId = getAttachmentId(candidate);
  const attachmentId = getAttachmentId(attachment);
  if (candidateId && attachmentId) {
    return candidateId === attachmentId;
  }

  const candidateDataUrl = getAttachmentDataUrl(candidate);
  const attachmentDataUrl = getAttachmentDataUrl(attachment);
  if (!candidateDataUrl || !attachmentDataUrl) return false;
  if (candidateDataUrl !== attachmentDataUrl) return false;

  const candidateMessageId = getAttachmentMessageId(candidate);
  const attachmentMessageId = getAttachmentMessageId(attachment);

  return (
    !candidateMessageId ||
    !attachmentMessageId ||
    candidateMessageId === attachmentMessageId
  );
};

export const findMatchingAttachment = (attachments, attachment) => {
  return (unref(attachments) || []).find(candidate =>
    matchesAttachment(candidate, attachment)
  );
};

const findInternalDownloadUrl = (attachments, attachment) => {
  return getInternalDownloadUrl(
    findMatchingAttachment(attachments, attachment)
  );
};

export function useResolveImageDownloadUrl() {
  const store = useStore();

  const getConversationAttachments = conversationId => {
    const normalizedConversationId = Number(unref(conversationId));
    return (
      store.state.conversations?.attachments?.[normalizedConversationId] || []
    );
  };

  const resolveImageDownloadUrl = async ({
    attachment,
    attachments = [],
    conversationId,
  }) => {
    const currentAttachment = unref(attachment);

    const directUrl = getInternalDownloadUrl(currentAttachment);
    if (directUrl) return directUrl;

    const listUrl = findInternalDownloadUrl(attachments, currentAttachment);
    if (listUrl) return listUrl;

    const storeUrl = findInternalDownloadUrl(
      getConversationAttachments(conversationId),
      currentAttachment
    );
    if (storeUrl) return storeUrl;

    const normalizedConversationId = Number(unref(conversationId));
    if (!normalizedConversationId) return '';

    await store.dispatch('fetchAllAttachments', normalizedConversationId);

    return findInternalDownloadUrl(
      getConversationAttachments(normalizedConversationId),
      currentAttachment
    );
  };

  return {
    resolveImageDownloadUrl,
  };
}
